import { splitCamelCase } from "../utils/otherUtils";
import { minimumAreaToPerimeterRatio, msaEmailAddress } from "./constants";
import { featureTypes, nvrToolModes, nvrTools, validations } from "./types";

const validationMessages = {
  [validations.invalidPolygon]: {
    uploadPatchMessage: "Patches with invalid geometry were found",
    uploadRevegetationMessage:
      "Revegetation areas with invalid geometry were found",
    getMessageTitle: (featureType: featureTypes) =>
      `Problem with ${splitCamelCase(featureType)} shape`,
    selfIntersectingPolygonMessage:
      "The polygon you have just drawn is self-intersecting. This type of polygon is not supported.",
    selfIntersectingPolygonAfterEditMessage:
      "This change would result in a self-intersecting polygon. This type of polygon is not supported.",
    getInvalidAfterMergeMessage: (featureType: featureTypes) =>
      `This change would result in a ${splitCamelCase(
        featureType
      )} that is self-intersecting or has a hole in the middle. This type of shape is not supported.`,
  },

  [validations.overlappingTypes]: {
    // upload validation handled in uploadUtils.ts
    getMessageTitle: (
      featureType: featureTypes,
      otherFeatureType: featureTypes
    ) =>
      `Overlapping ${splitCamelCase(featureType)} and ${splitCamelCase(
        otherFeatureType
      )}`,
    content: (featureType: featureTypes, otherFeatureType: featureTypes) => (
      <>
        You have mapped a {splitCamelCase(featureType)} overlapping with a{" "}
        {splitCamelCase(otherFeatureType)} which is not permitted. Please ensure
        there are no overlapping polygons.
      </>
    ),
  },

  [validations.msaLevyArea]: {
    uploadPatchMessage: "Patches intersect the MSA Levy Area",
    uploadTreeMessage: "Trees intersect the MSA Levy Area",
    uploadRevegetationMessage: "Revegetation areas intersect the MSA Levy Area",
    getMessageTitle: (featureType: featureTypes) =>
      `${splitCamelCase(featureType)} feature in MSA Levy Area`,
    content: (nvrToolMode: nvrToolModes) => (
      <>
        The location that you have selected falls under the Melbourne Strategic
        Assessment Levy Area and has specific native vegetation and habitat
        removal obligations.
        <p>
          If you wish to{" "}
          {nvrToolMode === nvrTools.removal ? "remove" : "protect"} native
          vegetation at this location, please contact{" "}
          <a
            href={`mailto:${msaEmailAddress}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {msaEmailAddress}
          </a>
        </p>
      </>
    ),
  },

  [validations.msaConservation]: {
    getMessageTitle: (featureType: featureTypes) =>
      `${splitCamelCase(featureType)} feature in MSA Conservation Areas`,
    content: () => (
      <>
        Your proposed native vegetation removal lies within one of the 36
        Conservation Areas established under the Melbourne Strategic Assessment
        (MSA) Program.{" "}
        <p>
          The Conservation Areas are excluded from the MSA levy liability
          calculations. Any new development or change in land-use within a
          Conservation Area will require approval. Please contact{" "}
          <a
            href={`mailto:${msaEmailAddress}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {msaEmailAddress}
          </a>{" "}
          regarding this requirement.
        </p>
        Any native vegetation proposed within the Conservation Areas is subject
        to assessment under the Native Vegetation Regulations.
      </>
    ),
  },

  [validations.msa12Precincts]: {
    getMessageTitle: (featureType: featureTypes) =>
      `${splitCamelCase(featureType)} feature in one of the MSA 12 Precincts`,
    content: () => (
      <>
        Your proposed native vegetation removal lies within one of the twelve
        precincts that are regulated under the Melbourne Strategic Assessment
        (MSA) Program. These precincts are excluded from the MSA Levy Area and
        any portion of land covering the twelve precincts is excluded from MSA
        levy liability calculations.
        <p>
          Unless an exemption applies, a permit application is required under
          Clause 52.16 of the Victoria Planning Provisions (VPP) and relevant
          planning scheme to remove native vegetation designated for retention
          in the relevant NVPP.
        </p>
        <p>
          Offset requirements relating to the removal of native vegetation
          designated for removal in the relevant NVPP can be satisfied either
          through the payment of biodiversity offset fees to DEECA through the
          MSA Program, or in accordance with the NVPP. Please contact{" "}
          <a
            href={`mailto:${msaEmailAddress}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {msaEmailAddress}
          </a>{" "}
          if you require a fee estimate.
        </p>
      </>
    ),
  },

  [validations.apRatio]: {
    getUploadMessage: () =>
      `Some of the uploaded revegetation areas do not meet the minimum area to perimeter ratio of ${minimumAreaToPerimeterRatio}. You need to make them bigger unless they are adjacent to a patch of native vegetation and the combined area to perimeter ratio of the patch plus the revegetation is at least ${minimumAreaToPerimeterRatio}.`,
    title: "Area to perimeter ratio",
    content: () => (
      <>
        The revegetation area does not meet the minimum area to perimeter ratio
        of {minimumAreaToPerimeterRatio}. You need to make it bigger unless it
        is adjacent to a patch of native vegetation and the combined area to
        perimeter ratio of the patch plus the revegetation is at least{" "}
        {minimumAreaToPerimeterRatio}.
      </>
    ),
  },

  [validations.treesBelowDbhThreshold]: {
    uploadMessage: "Some trees are too small to generate gain",
    editedPatchTitle: "Patch overlapping invalid tree",
    editedPatchContent: () => (
      <>
        You have mapped a Patch overlapping with a tree that does not meet the
        offset requirements for trees in patches. Your change will be discarded.
      </>
    ),
    editedTreeTitle: "Invalid tree",
    editedTreeContent: (inPatch: boolean) => (
      <>
        The edited tree is too small and does not meet the offset requirements
        for {inPatch ? "trees in patches" : "Scattered Trees"}. Your change will
        be discarded.
      </>
    ),
  },

  [validations.wetlands]: {
    uploadPatchMessage: `Some of the uploaded patches lie within a Current Wetland area mapped by DEECA, predicted to support native vegetation. 
    To ensure the permanently protected area supports native vegetation on-site, please ensure that at least 25% of the total perennial understorey plant cover within the site is native.`,
    uploadRevegetationMessage: `Some of the uploaded revegetation areas lie within a Current Wetland area mapped by DEECA`,
    patchTitle: undefined,
    patchContent: () => (
      <>
        The Patch you have drawn lies within a Current Wetland area mapped by
        DEECA, predicted to support native vegetation. To ensure the permanently
        protected area supports native vegetation on-site, please ensure that at
        least 25% of the total perennial understorey plant cover within the site
        is native.
      </>
    ),
    revegetationTitle: "Revegetation area in current wetland",
    revegetationContent: () => (
      <>
        The Revegetation area you have drawn lies within a Current Wetland area
        mapped by DEECA. Please contact DEECA if you wish to proceed with this
        proposal.
      </>
    ),
  },

  [validations.nonWoodyEvc]: {
    uploadMessage: `Revegetation is not permitted in non woody vegetation types`,
    title: "Revegetation in non woody EVC",
    content: () => (
      <>Revegetation is not permitted in non woody vegetation types.</>
    ),
  },

  [validations.parcelBoundaryProximity]: {
    uploadMessage: `Some of the uploaded features lie within six metres of a parcel boundary. While this is permitted, please consider your future land management requirements i.e. fuel breaks, access tracks etc.`,
    title: undefined,
    content: () => (
      <>
        The area you have mapped lies within six metres of a parcel boundary.
        While this is permitted, please consider your future land management
        requirements i.e. fuel breaks, access tracks etc.
      </>
    ),
  },

  [validations.outsideSupportingRasters]: {
    uploadMessage: `Uploaded features are outside Victoria, please ensure all features are within Victoria`,
    title: "Feature outside Victoria",
    content: () => (
      <>
        The feature you have mapped is outside Victoria and will be discarded.
      </>
    ),
  },
};

export default validationMessages;
